import React from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import { formatNumber } from '../../utils/transform';
import {
  formatMoneyCode,
  normalizeCurrencyCode,
} from '../../utils/currencyDisplay';

const columnHelper = createColumnHelper();

const STATUS_SCHEME = {
  Pendiente: 'orange',
  Pagado: 'green',
  Parcial: 'yellow',
};

function formatDate(value) {
  if (!value) return '—';
  const d = String(value).slice(0, 10);
  return d.split('-').reverse().join('/');
}

function resolveInvoiceCurrencyCode(row) {
  if (row.currency?.code) {
    return normalizeCurrencyCode(row.currency.code);
  }
  if (Number(row.currencyId) === 2) return 'USD';
  return 'UYU';
}

export default function PurchaseInvoicesTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  sorting,
  onSortingChange,
  onInvoiceClick,
}) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = React.useMemo(
    () =>
      columnsData.map((col) =>
        columnHelper.accessor(col.accessor, {
          ...getSortableColumnOptions(col),
          header: () => (
            <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
          ),
          cell: (info) => {
            const row = info.row.original;
            if (col.accessor === 'provider.name') {
              return (
                <Text fontSize="sm">{row.provider?.name || '—'}</Text>
              );
            }
            if (col.accessor === 'date' || col.accessor === 'expirationDate') {
              return <Text fontSize="sm">{formatDate(info.getValue())}</Text>;
            }
            if (col.accessor === 'totalAmount') {
              const v = info.getValue();
              const code = resolveInvoiceCurrencyCode(row);
              return (
                <Text fontSize="sm">
                  {v != null ? formatMoneyCode(v, code) : '—'}
                </Text>
              );
            }
            if (col.accessor === 'status') {
              const status = info.getValue() || '—';
              return (
                <Badge
                  colorScheme={STATUS_SCHEME[status] || 'gray'}
                  fontSize="xs"
                  textTransform="none"
                >
                  {status}
                </Badge>
              );
            }
            return (
              <Text fontSize="sm" fontWeight="medium">
                {info.getValue() ?? '—'}
              </Text>
            );
          },
        }),
      ),
    [columnsData],
  );

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  return (
    <PageTable flexDirection="column" w="100%" px="0px">
      <Box>
        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader
                      key={header.id}
                      header={header}
                      borderColor={borderColor}
                    />
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columnsData.length} borderColor="transparent">
                    <Text color="gray.500" py={6} textAlign="center">
                      No hay facturas de compra para mostrar
                    </Text>
                  </Td>
                </Tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Tr
                    key={row.id}
                    cursor={onInvoiceClick ? 'pointer' : undefined}
                    _hover={onInvoiceClick ? { bg: 'gray.50' } : undefined}
                    onClick={() =>
                      onInvoiceClick?.(row.original?.invoiceId, row.original)
                    }
                    title={onInvoiceClick ? 'Ver detalle de la factura' : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <AdminTableCell key={cell.id} cell={cell} />
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </PageTable.Scroll>
        <TablePagination
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Box>
    </PageTable>
  );
}
